import React from 'react';
import { Popover, useMediaQuery, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/de';
import { useTranslation } from '@askporter/config-provider';
import { CalendarContainer } from './components/CalendarContainer';
import { TriggerButton } from './components/TriggerButton';
import { COLORS, RADIUS } from './constants';
import { useDateRangeDisplay } from './hooks/useDateRangeDisplay';
import { useDateRangeHandlers } from './hooks/useDateRangeHandlers';
import { useDateRangeState } from './hooks/useDateRangeState';
import type { DateRangePickerButtonProps } from './types';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * A button that displays a date range and opens a popover with two calendars for selecting a date range.
 * Works exactly like MUI DateRangePicker - click first date for start, click second date for end.
 */
export const DateRangePickerButton: React.FC<DateRangePickerButtonProps> = ({
  startDate,
  endDate,
  onChange,
  t,
  placeholder,
  ariaLabel,
  disabled = false,
  minDate,
  maxDate,
  id,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { i18n } = useTranslation();
  const adapterLocale = i18n.language ? i18n.language.split('-')[0] : 'en';

  const state = useDateRangeState({ startDate, endDate }, adapterLocale);
  const handlers = useDateRangeHandlers(
    { startDate, endDate, onChange, minDate, maxDate, disabled },
    state,
    adapterLocale,
  );
  const display = useDateRangeDisplay({ startDate, endDate, placeholder, t }, adapterLocale);

  const popoverId = state.open ? `${id}-popover` : undefined;

  // Left calendar shows currentMonth, right calendar shows currentMonth + 1
  const leftMonth = state.currentMonth;
  const rightMonth = state.currentMonth.add(1, 'month');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <TriggerButton
        id={id}
        disabled={disabled}
        displayText={display.getDisplayText()}
        hasSelectedDates={display.hasSelectedDates}
        ariaLabel={ariaLabel || t('common:search_filters:date_range:button_label')}
        onClick={handlers.handleClick}
        ariaDescribedBy={popoverId}
      />

      <Popover
        id={popoverId}
        open={state.open}
        anchorEl={state.anchorEl}
        onClose={handlers.handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'center' : 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: `${RADIUS['2xl']}px`,
              boxShadow: `0px 20px 24px -4px ${COLORS.shadowXl01}, 0px 8px 8px -4px ${COLORS.shadowXl02}, 0px 3px 3px -1.5px ${COLORS.shadowXl03}`,
              mt: '4px',
              border: `1px solid ${COLORS.borderSecondaryAlt}`,
              overflow: 'hidden',
              ...(isMobile && {
                maxWidth: 'calc(100vw - 32px)',
                mx: 2,
              }),
            },
          },
        }}
        onMouseLeave={() => state.setHoveredDay(null)}
      >
        <CalendarContainer
          leftMonth={leftMonth}
          rightMonth={rightMonth}
          tempStartDate={state.tempStartDate}
          tempEndDate={state.tempEndDate}
          hoveredDay={state.hoveredDay}
          setHoveredDay={state.setHoveredDay}
          primaryColor={primaryColor}
          isMobile={isMobile}
          locale={adapterLocale}
          minDate={minDate}
          maxDate={maxDate}
          onDateClick={handlers.handleDateClick}
          onPrevMonth={handlers.handlePrevMonth}
          onNextMonth={handlers.handleNextMonth}
          startInputValue={state.startInputValue}
          endInputValue={state.endInputValue}
          startInputError={state.startInputError}
          endInputError={state.endInputError}
          onStartInputChange={handlers.handleStartInputChange}
          onEndInputChange={handlers.handleEndInputChange}
          onStartInputBlur={handlers.handleStartInputBlur}
          onEndInputBlur={handlers.handleEndInputBlur}
          onStartInputKeyDown={handlers.handleStartInputKeyDown}
          onEndInputKeyDown={handlers.handleEndInputKeyDown}
          onApply={handlers.handleApply}
          onCancel={handlers.handleCancel}
          t={t}
        />
      </Popover>
    </LocalizationProvider>
  );
};

export type { DateRangePickerButtonProps } from './types';
